import { useState } from "react";
import { useModalStore } from "../../../stores/modalStore.js";
import { loadAppearance, saveAppearance, DEFAULTS } from "../../../hooks/useAppearance.js";

const THEMES = [
  { value: 'dark',   label: 'Dark',   desc: 'Default dark theme',   bg: '#0d0d14', accent: '#6366f1', text: '#e2e8f0' },
  { value: 'light',  label: 'Light',  desc: 'Light theme',          bg: '#f1f5f9', accent: '#6366f1', text: '#1e1f2e' },
  { value: 'amoled', label: 'AMOLED', desc: 'Pure black (OLED)',    bg: '#000000', accent: '#6366f1', text: '#e2e8f0' },
];

const FONT_SIZES = [
  { value: 12, label: 'Small',   sample: '12px' },
  { value: 14, label: 'Default', sample: '14px' },
  { value: 16, label: 'Large',   sample: '16px' },
];

const DENSITIES = [
  { value: 'cozy',    label: 'Cozy',    desc: 'Full avatars, comfortable spacing', icon: '🖼️' },
  { value: 'compact', label: 'Compact', desc: 'No avatars, tighter layout',        icon: '📋' },
];

export function AppearanceModal() {
  const { isOpen, type, onClose } = useModalStore();
  const [prefs, setPrefs] = useState(loadAppearance);
  const [saved, setSaved] = useState(false);

  if (!isOpen || type !== 'appearance') return null;

  const update = (patch) => {
    const next = { ...prefs, ...patch };
    setPrefs(next);
    saveAppearance(next); // live preview
  };

  const handleReset = () => { update(DEFAULTS); };
  const handleSave  = () => { saveAppearance(prefs); setSaved(true); setTimeout(() => setSaved(false), 2000); };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }} onClick={onClose}>
      <div style={{ width: '100%', maxWidth: 560, maxHeight: '90vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-primary,#13141f)', borderRadius: 16, boxShadow: '0 24px 64px rgba(0,0,0,0.6)', overflow: 'hidden' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#fff' }}>🎨 Appearance</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>Customize how AeThex Connect looks</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: 18, cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 28 }}>

          {/* Theme */}
          <section>
            <Label>Theme</Label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
              {THEMES.map(t => (
                <button key={t.value} onClick={() => update({ theme: t.value })}
                  style={{ padding: '14px 10px', borderRadius: 10, border: `2px solid ${prefs.theme === t.value ? 'var(--color-primary,#6366f1)' : 'rgba(255,255,255,0.1)'}`, cursor: 'pointer', transition: 'all 0.15s', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                  {/* Mini preview */}
                  <div style={{ width: 60, height: 40, borderRadius: 6, background: t.bg, border: '1px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'flex-end', padding: '0 0 6px 6px', overflow: 'hidden' }}>
                    <div style={{ width: 16, height: 16, borderRadius: '50%', background: t.accent }} />
                    <div style={{ flex: 1, margin: '0 4px', display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <div style={{ height: 3, background: t.text, opacity: 0.5, borderRadius: 1 }} />
                      <div style={{ height: 3, background: t.text, opacity: 0.3, borderRadius: 1, width: '60%' }} />
                    </div>
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: prefs.theme === t.value ? '#a5b4fc' : 'rgba(255,255,255,0.6)' }}>{t.label}</div>
                </button>
              ))}
            </div>
          </section>

          {/* Font size */}
          <section>
            <Label>Font Size</Label>
            <div style={{ display: 'flex', gap: 8 }}>
              {FONT_SIZES.map(f => (
                <button key={f.value} onClick={() => update({ fontSize: f.value })}
                  style={{ flex: 1, padding: '10px 8px', borderRadius: 10, border: `2px solid ${prefs.fontSize === f.value ? 'var(--color-primary,#6366f1)' : 'rgba(255,255,255,0.1)'}`, cursor: 'pointer', transition: 'all 0.15s', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <span style={{ fontSize: f.value, fontWeight: 600, color: '#fff', lineHeight: 1 }}>Aa</span>
                  <span style={{ fontSize: 11, color: prefs.fontSize === f.value ? '#a5b4fc' : 'rgba(255,255,255,0.5)' }}>{f.label}</span>
                </button>
              ))}
            </div>
            {/* Live preview */}
            <div style={{ marginTop: 12, padding: '12px 14px', borderRadius: 8, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginBottom: 6 }}>Preview</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg,#6366f1,#06b6d4)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: '#fff' }}>A</div>
                <div>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>AeThex </span>
                  <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>Today at 3:42 PM</span>
                  <div style={{ fontSize: prefs.fontSize, color: 'rgba(255,255,255,0.8)', marginTop: 2 }}>Welcome to AeThex Connect! This is how messages will look.</div>
                </div>
              </div>
            </div>
          </section>

          {/* Message density */}
          <section>
            <Label>Message Display</Label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {DENSITIES.map(d => (
                <label key={d.value}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 10, cursor: 'pointer', transition: 'all 0.15s',
                    background: prefs.density === d.value ? 'rgba(99,102,241,0.12)' : 'rgba(255,255,255,0.03)',
                    border: `2px solid ${prefs.density === d.value ? 'rgba(99,102,241,0.4)' : 'rgba(255,255,255,0.08)'}` }}>
                  <input type="radio" name="density" checked={prefs.density === d.value} onChange={() => update({ density: d.value })} style={{ accentColor: '#6366f1' }} />
                  <span style={{ fontSize: 20 }}>{d.icon}</span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>{d.label}</div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>{d.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </section>

          {/* Color saturation */}
          <section>
            <Label>Color Saturation — {prefs.saturation}%</Label>
            <input type="range" min={60} max={130} step={5} value={prefs.saturation} onChange={e => update({ saturation: parseInt(e.target.value) })}
              style={{ width: '100%', accentColor: '#6366f1', cursor: 'pointer' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'rgba(255,255,255,0.3)', marginTop: 4 }}>
              <span>Muted</span><span>Default</span><span>Vivid</span>
            </div>
          </section>

          {/* Toggles */}
          <section>
            <Label>Accessibility</Label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <ToggleRow label="Streamer Mode" sub="Hides server names, invite links, and DM names" checked={prefs.streamer} onChange={v => update({ streamer: v })} />
              <ToggleRow label="Reduce Motion" sub="Disables animations and transitions" checked={prefs.reducedMotion} onChange={v => update({ reducedMotion: v })} />
            </div>
          </section>
        </div>

        <div style={{ padding: '14px 24px', borderTop: '1px solid rgba(255,255,255,0.07)', display: 'flex', justifyContent: 'space-between', flexShrink: 0 }}>
          <button onClick={handleReset}
            style={{ padding: '8px 16px', borderRadius: 8, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)', fontSize: 12, cursor: 'pointer' }}>
            Reset Defaults
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} style={{ padding: '8px 18px', borderRadius: 8, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)', fontSize: 13, cursor: 'pointer' }}>Close</button>
            <button onClick={handleSave}
              style={{ padding: '8px 20px', borderRadius: 8, background: saved ? '#22c55e' : 'var(--color-primary,#6366f1)', border: 'none', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'background 0.2s' }}>
              {saved ? '✓ Saved' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Label({ children }) {
  return <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>{children}</div>;
}

function ToggleRow({ label, sub, checked, onChange }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div>
        <div style={{ fontSize: 13, color: '#fff', fontWeight: 500 }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>{sub}</div>}
      </div>
      <button type="button" onClick={() => onChange(!checked)}
        style={{ width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer', transition: 'background 0.2s', position: 'relative', background: checked ? 'var(--color-primary,#6366f1)' : 'rgba(255,255,255,0.15)' }}>
        <span style={{ position: 'absolute', top: 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', left: checked ? 23 : 3 }} />
      </button>
    </div>
  );
}
